import {
  useState,
  useMemo,
  useLayoutEffect,
  ChangeEvent,
  useRef,
  useEffect,
} from "react";
import { renderToString } from "react-dom/server";

import { useLocation } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import clsx from "clsx";

import { dataFaq } from "@data/index";
import { useUserStore } from "@stores/index";
import { QUERY_WIDTHS } from "@utils/index";
import { useClickOutside } from "@hooks/index";
import {
  MobileHeader,
  ChevronDownSvg,
  SearchSvg,
  XSvg,
  SearchFaqItem,
} from "@components/index";

interface ItemType {
  category: string;
  title: string;
  text: string;
}

export const FaqPage = () => {
  const location = useLocation();
  const isMobile = useMediaQuery({ query: QUERY_WIDTHS.md });

  const businesses = useUserStore(({ businesses }) => businesses);

  const [expandedId, setExpandedId] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [items, setItems] = useState<ItemType[]>([]);

  const searchFaqRef = useRef(null);
  useClickOutside(searchFaqRef, () => setShowSearch(false));

  const userBusinessTypes = useMemo(
    () => businesses.map(({ type }) => type),
    [businesses],
  );

  useLayoutEffect(() => {
    if (location.hash && businesses) {
      const id = location.hash.replaceAll("%20", " ").slice(1);
      const el = document.getElementById(id);
      el?.scrollIntoView();
      if (id.includes("---")) {
        setExpandedId(id);
        setShowSearch(false);
      }
    }
  }, [location, businesses]);

  useEffect(() => {
    if (searchValue.length < 2) {
      setItems([]);
      return;
    }

    const formattedFaq = dataFaq.reduce((acc, { category, items }) => {
      const formattedItems = items.map(({ title, content }) => {
        const contentToText = renderToString(content);
        const textWithoutHtml = contentToText.replace(/(<([^>]+)>)/gi, "");
        const textWithoutBullet = textWithoutHtml.replaceAll("•", "");
        const textWithoutArrow = textWithoutBullet.replaceAll("(&gt; )", ">");
        const text = textWithoutArrow.slice(0, 150);
        return { category, title, text };
      });

      return [...acc, ...formattedItems];
    }, [] as ItemType[]);

    const filteredResults = formattedFaq.filter(
      ({ title, text }) =>
        title.toUpperCase().includes(searchValue.toUpperCase()) ||
        text.toUpperCase().includes(searchValue.toUpperCase()),
    );
    setItems(filteredResults);
  }, [searchValue]);

  const handleClick = (category: string, title: string) =>
    setExpandedId(
      expandedId === `${category}---${title}` ? "" : `${category}---${title}`,
    );

  const handleSearchChange = ({ target }: ChangeEvent<HTMLInputElement>) =>
    setSearchValue(target.value);

  return (
    <>
      {isMobile && <MobileHeader href="/" title="FAQ's" />}
      <div className="min-h-[calc(100dvh_-_50px)] rounded-b-lg bg-lightGray px-4 pb-6 pt-0 text-black-100 md:min-h-[calc(100dvh_-_152px)] md:px-10 md:pt-6">
        <div ref={searchFaqRef} className="flex w-full gap-x-6 pt-6 md:pt-0">
          {!isMobile && <h1 className="text-600 font-bold">FAQ's</h1>}
          <div className="relative mr-0 w-full max-w-[812px] lg:mr-[360px]">
            <div className="flex h-[48px] items-center justify-between rounded-lg bg-lessLightGray">
              {searchValue || showSearch ? (
                <input
                  name="searchFaq"
                  value={searchValue}
                  onFocus={() => setShowSearch(true)}
                  onChange={handleSearchChange}
                  autoFocus
                  className="w-full border-0 bg-transparent pl-4 pr-12 font-bold text-black-100 outline-none"
                />
              ) : (
                <div
                  className="pl-4 font-bold text-black-32 *:text-magenta-200"
                  onClick={() => setShowSearch(true)}
                >
                  <span>Search FAQ:</span> Please enter at least 2 characters
                </div>
              )}
              <div className="mr-4 text-magenta-200">
                {!searchValue ? (
                  <SearchSvg />
                ) : (
                  <button type="button" onClick={() => setSearchValue("")}>
                    <XSvg />
                  </button>
                )}
              </div>
            </div>
            {showSearch && (
              <div className="absolute top-14 z-10 flex max-h-64 w-full flex-col gap-y-2 overflow-y-auto rounded-lg bg-white-100 py-4 shadow-sm">
                {!items?.length ? (
                  <SearchFaqItem
                    noHover
                    category=""
                    title="No results found."
                    text="Try different keywords"
                  />
                ) : (
                  items.map(({ category, title, text }, index) => (
                    <SearchFaqItem
                      key={index}
                      category={category}
                      title={title}
                      text={text}
                      searchValue={searchValue}
                    />
                  ))
                )}
              </div>
            )}
          </div>
        </div>

        <div className="flex justify-between gap-x-10">
          <div className="w-full max-w-[920px]">
            {dataFaq.map(({ category, businessTypes, items }) =>
              !businessTypes?.length ||
              userBusinessTypes.find((type) =>
                businessTypes?.includes(type),
              ) ? (
                <div key={category} className="pt-6">
                  <div id={category} className="text-400 font-bold">
                    {category}
                  </div>
                  <div className="flex flex-col gap-y-2 pt-6">
                    {items.map(({ title, content }) => (
                      <div
                        key={`${category}---${title}`}
                        id={`${category}---${title}`}
                        className="rounded-lg bg-white-100 p-6"
                      >
                        <div
                          className="flex cursor-pointer items-center justify-between"
                          onClick={() => handleClick(category, title)}
                        >
                          <div className="text-300 font-bold">{title}</div>
                          <div
                            className={clsx(
                              "text-black-54",
                              expandedId === `${category}---${title}` &&
                                "rotate-180",
                            )}
                          >
                            <ChevronDownSvg />
                          </div>
                        </div>
                        <div
                          className={clsx(
                            "pt-2 text-300 font-light text-black-54",
                            "[&_a]:text-primary-300 [&_a]:underline",
                            expandedId === `${category}---${title}`
                              ? "block"
                              : "hidden",
                          )}
                        >
                          {content}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : null,
            )}
          </div>

          <div className="sticky top-0 mt-20 hidden h-fit w-80 shrink-0 rounded-lg bg-white-100 py-3 lg:block">
            {dataFaq.map(({ category, businessTypes }) =>
              !businessTypes?.length ||
              userBusinessTypes.find((type) =>
                businessTypes?.includes(type),
              ) ? (
                <a
                  key={category}
                  href={`#${category}`}
                  className={clsx(
                    "block px-6 py-3 font-bold hover:text-black-100",
                    location.hash.replaceAll("%20", " ").slice(1) === category
                      ? "text-black-100"
                      : "text-black-54",
                  )}
                >
                  {category}
                </a>
              ) : null,
            )}
          </div>
        </div>
      </div>
    </>
  );
};
